import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { TokenViewTab } from "../types/tokenViewTab";
import { AuthMode } from "../types/authMode";
import { LoanPool } from "../types/loanTypes";


interface UIState {
  profileDialogOpen: boolean;
  profileSection: "details" | "security" | "nfts" | "gifts" | "contacts" | null;
  walletSection: WalletSection
  selectedChatView: ChatView
  selectedMarketplaceTab: MarketView;
  selectedWalletTab: WalletView;
  selectedWelcomeTab: WelcomeTab;
  currentNavigationView: NavigationView;
  selectedSettingsTab: SettingsView,
  selectedDenomination: string;
  userSelectedAmount: string;
  selectedNFTTab: TokenViewTab;
  authMode:AuthMode;
  countrySelectDrawerOpen?: boolean;
  timezone: string,
  messagingEnabled?: boolean;
  isCompletingLogin: boolean;
  currentMooloanView: LoanView;
  mooLoansEnabled?: boolean;
  selectedAssetTab: AssetTab;
  currentLoanPoolView?: LoanViewType;
  selectedLoanPool?: LoanPool | null;
}
const initialState: UIState = {
  profileDialogOpen: false,
  profileSection: 'details',
  walletSection: "main" as WalletSection,
  selectedWelcomeTab: "wallets" as WelcomeTab,
  selectedChatView: "list" as ChatView,
  selectedMarketplaceTab: "list" as MarketView,
  selectedWalletTab: "main" as WalletView,
  currentNavigationView: "chat" as NavigationView,
  selectedSettingsTab: "details" as SettingsView,
  selectedDenomination: "",
  userSelectedAmount: "0",
  selectedNFTTab: "list" as TokenViewTab,
  authMode: "login" as AuthMode,
  countrySelectDrawerOpen: false,
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  messagingEnabled: false, 
  isCompletingLogin: false,
  currentMooloanView: "investment" as LoanView, 
  mooLoansEnabled: true, 
  selectedAssetTab: "nfts" as AssetTab,
  currentLoanPoolView: "list" as LoanViewType,
  selectedLoanPool: null, 
};

const uiSlice = createSlice({
  name: "ui",
  initialState,
  reducers: {
    openProfileDialog: (state: UIState) => {
      state.profileDialogOpen = true;
    },
    closeProfileDialog: (state: UIState) => {
      state.profileDialogOpen = false;
    },
    setProfileSection: (state: UIState, action) => {
      state.profileSection = action.payload;
    },
    setWalletSection: (state: UIState, action:PayloadAction<WalletSection>) => {
      state.walletSection = action.payload;
    },
    setSelectedWelcomeTab: (state: UIState, action:PayloadAction<WelcomeTab>) => {
      state.selectedWelcomeTab = action.payload;
    },
    setSelectedChatView: (state: UIState, action: PayloadAction<ChatView>) => {
      state.selectedChatView = action.payload;
    },
    setSelectedMarketplaceTab: (state: UIState, action: PayloadAction<MarketView>) => {
      state.selectedMarketplaceTab = action.payload;
    },
    setSelectedWalletTab: (state: UIState, action: PayloadAction<WalletView>) => {
      state.selectedWalletTab = action.payload;
    },
    setCurrentNavigationView: (state: UIState, action: PayloadAction<NavigationView>) => {
      state.currentNavigationView = action.payload;
    },
    setSelectedSettingsTab: (state: UIState, action: PayloadAction<SettingsView>) => {
      state.selectedSettingsTab = action.payload;
    },
    setSelectedDenomination: (state: UIState, action: PayloadAction<string>) => {
      state.selectedDenomination = action.payload;
    },
    setCustomAmount: (state: UIState, action: PayloadAction<string>) => {
      state.userSelectedAmount = action.payload;
    },
    setSelectedNFTTab: (state: UIState, action: PayloadAction<TokenViewTab>) => {
      state.selectedNFTTab = action.payload;
    },
    setAuthMode: (state: UIState, action: PayloadAction<AuthMode>) => {
      state.authMode = action.payload;
    },
    setMessagingEnabled: (state: UIState, action: PayloadAction<boolean>) => {
      state.messagingEnabled = action.payload;
      
    },
    setMooLoansEnabled: (state: UIState, action: PayloadAction<boolean>) => {
      state.mooLoansEnabled = action.payload;
    },
    setIsCompletingLogin: (state: UIState, action: PayloadAction<boolean>) => {
      state.isCompletingLogin = action.payload;
    },
    setCurrentMooloanView: (state: UIState, action: PayloadAction<LoanView>) => {
      state.currentMooloanView = action.payload;
    },
    setSelectedAssetTab: (state: UIState, action: PayloadAction<AssetTab>) => {
      state.selectedAssetTab = action.payload as AssetTab;
    },
    setCurrentLoanPoolView: (state: UIState, action: PayloadAction<LoanViewType>) => {
      state.currentLoanPoolView = action.payload;
    },
    setSelectedLoanPool: (state: UIState, action: PayloadAction<LoanPool | null>) => {
      state.selectedLoanPool = action.payload;
    },
    toggleCountrySelectDrawerOpen: (state: UIState) => {
      state.countrySelectDrawerOpen = !state.countrySelectDrawerOpen;
    },
  },
});

export const { openProfileDialog, 
  closeProfileDialog ,
  setProfileSection,
  setWalletSection,
  setSelectedChatView,
  setSelectedMarketplaceTab,
  setSelectedWalletTab,
  setCurrentNavigationView,
  setSelectedSettingsTab,
  setSelectedDenomination,
  setCustomAmount,
  setSelectedNFTTab,
  setAuthMode,
  toggleCountrySelectDrawerOpen,
  setMessagingEnabled,
  setIsCompletingLogin,
  setCurrentMooloanView,
  setMooLoansEnabled,
  setSelectedAssetTab,
  setCurrentLoanPoolView,
  setSelectedLoanPool,
  setSelectedWelcomeTab,
} = uiSlice.actions;
export default uiSlice.reducer;